import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const JourneyContext = createContext(null);

// Journey steps in order. `path` is where the step's page lives.
export const STEP_META = [
  { key: "profil", title: "Profil Bisnis", path: "/assessment" },
  { key: "ide", title: "Ide Bisnis", path: "/ideas" },
  { key: "pilih", title: "Pilih 2 Ide", path: "/ideas" },
  { key: "validasi", title: "Validasi", path: "/validation" },
  { key: "winner", title: "Business Winner", path: "/comparison" },
  { key: "blueprint", title: "Business Blueprint", path: "/blueprint" },
  { key: "launch", title: "Rencana 7 Hari", path: "/launch" },
];

export function JourneyProvider({ children }) {
  const { user } = useAuth();
  const hasAccess = !!user && (user.access_status === "active" || user.access_status === "admin");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    assessment: null, ideas: null, validation: null,
    winner: null, blueprint: null, launch: null,
  });

  const load = useCallback(async () => {
    // Locked (trial/expired) users have no journey data and every endpoint 403s;
    // skip the calls entirely.
    if (!hasAccess) {
      setData({ assessment: null, ideas: null, validation: null, winner: null, blueprint: null, launch: null });
      setLoading(false);
      return;
    }
    const [a, i, v, w, b, l] = await Promise.all([
      api.get("/assessment").catch(() => ({ data: null })),
      api.get("/ideas").catch(() => ({ data: null })),
      api.get("/validation").catch(() => ({ data: null })),
      api.get("/winner").catch(() => ({ data: null })),
      api.get("/blueprint").catch(() => ({ data: null })),
      api.get("/launch").catch(() => ({ data: null })),
    ]);
    setData({
      assessment: a.data, ideas: i.data, validation: v.data,
      winner: w.data, blueprint: b.data, launch: l.data,
    });
    setLoading(false);
  }, [hasAccess]);

  useEffect(() => { load(); }, [load]);

  const { assessment, ideas, validation, winner, blueprint, launch } = data;

  const profileDone = assessment?.assessment_status === "completed";
  const hasIdeas = !!ideas?.ideas?.length;
  const ideaCount = ideas?.ideas?.length || 0;
  const valStatus = validation?.status || null;
  const selectedIdeas = validation?.ideas || [];
  const valCompleted = valStatus === "completed";
  const hasWinner = !!winner?.winner_name;
  const hasBlueprint = !!blueprint?.winner_name;
  const hasLaunch = !!launch?.days?.length;
  const launchDone = launch?.completed_days?.length || 0;

  // state: completed | active | ready | locked
  const states = [
    profileDone ? "completed" : "active",
    !profileDone ? "locked" : hasIdeas ? "completed" : "ready",
    !hasIdeas ? "locked" : valStatus ? "completed" : "ready",
    !valStatus ? "locked" : valCompleted ? "completed" : "ready",
    !valCompleted ? "locked" : hasWinner ? "completed" : "ready",
    !hasWinner ? "locked" : hasBlueprint ? "completed" : "ready",
    !hasBlueprint ? "locked" : hasLaunch ? "completed" : "ready",
  ];

  let resume;
  if (!profileDone) resume = { label: assessment ? "Lanjutkan Profil Bisnis" : "Mulai Profil Bisnis", to: "/assessment" };
  else if (!hasIdeas) resume = { label: "Temukan Ide Bisnis", to: "/ideas" };
  else if (!valStatus) resume = { label: "Pilih 2 Ide", to: "/ideas" };
  else if (!valCompleted) resume = { label: "Lanjutkan Validasi", to: "/validation" };
  else if (!hasWinner) resume = { label: "Tentukan Business Winner", to: "/comparison" };
  else if (!hasBlueprint) resume = { label: "Buat Business Blueprint", to: "/blueprint" };
  else if (!hasLaunch) resume = { label: "Buat Rencana 7 Hari", to: "/launch" };
  else resume = { label: "Lihat Hasil Anda", to: "/launch", complete: true };

  const currentStep = states.findIndex((s) => s === "active" || s === "ready");
  const stepNumber = currentStep === -1 ? 7 : currentStep + 1;

  const value = {
    loading, refresh: load,
    assessment, ideas, validation, winner, blueprint, launch,
    profileDone, hasIdeas, ideaCount, valStatus, selectedIdeas, valCompleted,
    hasWinner, hasBlueprint, hasLaunch, launchDone,
    states, resume, stepNumber,
  };

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}

export function useJourney() {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error("useJourney must be used within JourneyProvider");
  return ctx;
}
